// Credentials (FD §19): the list, plus connecting GitHub.
//
// SECURITY: this page must never render a secret value. The API's
// credentials response (CredentialSummary, see lib/api-client.ts) already
// excludes ciphertext/iv/authTag (packages/api/src/db/schema/credentials.ts
// forbids ever selecting them into a response) — this component must not be
// changed to fetch or display raw secret material, even for debugging.

import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  credentialsApi,
  mcpServicesApi,
  type CredentialSummary,
} from '../lib/api-client'
import { useWorkspaceId } from '../lib/auth'
import { StatusBadge } from '../components/status-badge'
import { ExhaustionBadge } from '../components/exhaustion-badge'
import { GitHubPanel } from '../components/github-connect'
import {
  EmptyState,
  ErrorNote,
  LoadingRows,
  PageHeader,
} from '../components/ui'
import { formatRelative, humanize } from '../lib/format'

function kindLabel(credential: CredentialSummary): string {
  if (credential.type === 'github_app') return 'GitHub App'
  if (credential.scope?.provider === 'github') return 'GitHub token'
  if (credential.scope?.purpose === 'mcp_service') return 'MCP server secret'
  return humanize(credential.type)
}

export function CredentialsPage() {
  const workspaceId = useWorkspaceId()
  const credentialsQuery = useQuery({
    queryKey: ['credentials', workspaceId],
    queryFn: () => credentialsApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const credentials = credentialsQuery.data?.items ?? []
  // An MCP server's secret is named after the server's id; show its name.
  const mcpQuery = useQuery({
    queryKey: ['mcp-services', workspaceId],
    queryFn: () => mcpServicesApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const mcpNames = new Map(
    (mcpQuery.data?.items ?? []).map((service) => [service.id, service.name])
  )
  const nameOf = (credential: CredentialSummary): string => {
    const serviceId = credential.scope?.mcpServiceId
    const serviceName =
      typeof serviceId === 'string' ? mcpNames.get(serviceId) : undefined
    return serviceName ? `${serviceName} (MCP server)` : credential.name
  }

  return (
    <div>
      <PageHeader
        title="Credentials"
        description="Logins and tokens Goblic holds for your agents. They’re stored encrypted and never shown, not even here."
      />
      <GitHubPanel workspaceId={workspaceId} />
      <div className="panel overflow-x-auto">
        {credentialsQuery.isLoading ? (
          <LoadingRows />
        ) : credentialsQuery.isError ? (
          <ErrorNote error={credentialsQuery.error} what="credentials" />
        ) : credentials.length === 0 ? (
          <EmptyState
            title="No credentials yet"
            action={
              <Link to="/executors" className="btn-secondary">
                Go to executors
              </Link>
            }
          >
            Connecting an executor adds its login here.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Last verified</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((credential) => (
                <tr key={credential.id}>
                  <td className="font-medium text-ink">{nameOf(credential)}</td>
                  <td>{kindLabel(credential)}</td>
                  <td>
                    <StatusBadge status={credential.status} />
                    <ExhaustionBadge exhaustion={credential} />
                  </td>
                  <td className="whitespace-nowrap">
                    {credential.lastVerifiedAt
                      ? formatRelative(credential.lastVerifiedAt)
                      : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/*
        TODO: connect/revoke actions (FD §19.1). "Connect" must open a form
        that posts the secret directly to the API and never round-trips it
        back to this page for display; "revoke" only ever needs the
        credential id, never its value.
      */}
    </div>
  )
}
